import { useCallback, useRef, useState } from "react";

const STORAGE_KEY_PREFIX = "RANK_TRACKER_";

type SetPersistentState<T> = (value: T) => void;
type ClearPersistentState = () => void;
type UsePersistentStateReturn<T> = [T | undefined, SetPersistentState<T>, ClearPersistentState];

/**
 * Hook for persisting state to localStorage.
 * Automatically prefixes keys with "RANK_TRACKER_".
 *
 * @param key - Storage key (will be prefixed with "RANK_TRACKER_")
 * @param defaultValue - Default value when nothing is stored
 * @returns [state, setState, clearState]
 */
export const usePersistentState = <T>(key: string, defaultValue?: T): UsePersistentStateReturn<T> => {
    const storageKey = `${STORAGE_KEY_PREFIX}${key}`;
    const defaultValueRef = useRef(defaultValue);

    const [state, setStateInternal] = useState<T | undefined>(() => {
        try {
            const stored = localStorage.getItem(storageKey);
            return stored !== null ? (JSON.parse(stored) as T) : defaultValue;
        } catch {
            return defaultValue;
        }
    });

    const setState = useCallback(
        (value: T) => {
            try {
                localStorage.setItem(storageKey, JSON.stringify(value));
                setStateInternal(value);
            } catch {
                // localStorage not available or quota exceeded
            }
        },
        [storageKey],
    );

    const clearState = useCallback(() => {
        try {
            localStorage.removeItem(storageKey);
            setStateInternal(defaultValueRef.current);
        } catch {
            // localStorage not available
        }
    }, [storageKey]);

    return [state, setState, clearState];
};
